import React, { forwardRef, useRef, useImperativeHandle, useState } from 'react';
import { Switch } from '@heroui/react';
import { Scatter } from 'react-chartjs-2';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler
} from 'chart.js';
import { BarChart3 } from 'lucide-react';

// Register Chart.js components
ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler
);

// Marker shapes per wbPoint label
const WB_POINT_STYLES = {
	Ramp: 'circle',
	Takeoff: 'rect',
	Landing: 'triangle'
};

// Min / Max fuel case visibility (USER toggle), remembered per browser. The print
// manifest captures this same canvas, so it shows exactly what is on screen.
const FUEL_CASES_STORAGE_KEY = 'wbEnvelope.fuelCases';
const DEFAULT_FUEL_CASES = { min: true, max: true };

const readFuelCases = () => {
	try {
		const saved = JSON.parse(window.localStorage.getItem(FUEL_CASES_STORAGE_KEY) || 'null');
		return saved && typeof saved === 'object'
			? { min: saved.min !== false, max: saved.max !== false }
			: DEFAULT_FUEL_CASES;
	} catch {
		return DEFAULT_FUEL_CASES;
	}
};

const saveFuelCases = value => {
	try {
		window.localStorage.setItem(FUEL_CASES_STORAGE_KEY, JSON.stringify(value));
	} catch {
		/* storage unavailable: the toggle still works for this page */
	}
};

// Default unit labels
const DEFAULT_UNIT_LABELS = { weight: 'lbs', cg: 'in' };

// Unit label display strings
const unitDisplayStrings = {
	weight: { lbs: 'lbs', kg: 'kg' },
	cg: { in: 'inches', m: 'meters' }
};

const EnvelopeChart = forwardRef(function EnvelopeChart(
	// `height` — wrapper height (CSS value). Pass '100%' to fill a parent with
	// a definite height (e.g. the W&B aspect-square panel); default keeps the
	// historical fixed 380px for the other call sites.
	{ forwardLimits, aftLimits, fuelCasePoints, wbPoints, unitLabels, height = 380 },
	ref
) {
	// Holds the underlying Chart.js instance (react-chartjs-2 forwards it via ref).
	const chartRef = useRef(null);
	const [visibleCases, setVisibleCases] = useState(readFuelCases);
	const toggleCase = (key, isOn) =>
		setVisibleCases(prev => {
			const next = { ...prev, [key]: isOn };
			saveFuelCases(next);
			return next;
		});

	// Expose an imperative capture so the print manifest can embed a live
	// screenshot of the on-screen envelope. Returns a PNG data URL, or `null`
	// (non-throwing) when the chart isn't mounted/ready — callers fall back to
	// the previously uploaded image.
	useImperativeHandle(
		ref,
		() => ({
			toImage() {
				const chart = chartRef.current;
				if (!chart || !chart.canvas) return null;
				try {
					// Stabilize before capture: halt any in-flight animation and
					// render the final frame synchronously so we never grab a
					// mid-animation frame.
					chart.stop();
					chart.update('none');

					const srcCanvas = chart.canvas;
					// Size the offscreen canvas to the DPR-scaled RENDER dimensions
					// (canvas.width/height), NOT clientWidth/clientHeight — keeps the
					// printed image crisp on high-DPI displays.
					const offscreen = document.createElement('canvas');
					offscreen.width = srcCanvas.width;
					offscreen.height = srcCanvas.height;

					const ctx = offscreen.getContext('2d');
					if (!ctx) return null;

					// The chart canvas is transparent; fill white first so the print
					// image has a solid background instead of print artifacts.
					ctx.fillStyle = '#ffffff';
					ctx.fillRect(0, 0, offscreen.width, offscreen.height);
					ctx.drawImage(srcCanvas, 0, 0);

					return offscreen.toDataURL('image/png');
				} catch {
					return null;
				}
			},
		}),
		[]
	);

	const units = unitLabels || DEFAULT_UNIT_LABELS;
	const weightUnit = units.weight || 'lbs';
	const cgUnit = units.cg || 'in';

	// Check if we're in dark mode
	const isDark = document.documentElement.classList.contains('dark');

	// Theme-aware colors
	const gridColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
	const textColor = isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)';
	const borderColor = isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';

	// Create envelope polygon by connecting forward and aft limits
	const createEnvelopePolygon = () => {
		const validForward = forwardLimits.filter(
			l => parseFloat(l.minWeight) > 0 && parseFloat(l.minWeightCgLimit) > 0
		);
		const validAft = aftLimits.filter(
			l => parseFloat(l.minWeight) > 0 && parseFloat(l.minWeightCgLimit) > 0
		);

		if (validForward.length === 0 || validAft.length === 0) return [];

		const points = [];

		// Sort forward limits by weight ascending for left boundary
		const sortedForward = [...validForward].sort(
			(a, b) => parseFloat(a.minWeight) - parseFloat(b.minWeight)
		);
		// Sort aft limits by weight ascending for right boundary
		const sortedAft = [...validAft].sort(
			(a, b) => parseFloat(a.minWeight) - parseFloat(b.minWeight)
		);

		// Create left boundary (forward limits) - bottom to top
		sortedForward.forEach(limit => {
			const minW = parseFloat(limit.minWeight);
			const minCg = parseFloat(limit.minWeightCgLimit);
			const maxW = parseFloat(limit.maxWeight);
			const maxCg = parseFloat(limit.maxWeightCgLimit);

			points.push({ x: minCg, y: minW });
			if (maxW > minW && maxCg > 0) {
				points.push({ x: maxCg, y: maxW });
			}
		});

		// Create right boundary (aft limits) - top to bottom
		[...sortedAft].reverse().forEach(limit => {
			const minW = parseFloat(limit.minWeight);
			const minCg = parseFloat(limit.minWeightCgLimit);
			const maxW = parseFloat(limit.maxWeight);
			const maxCg = parseFloat(limit.maxWeightCgLimit);

			if (maxW > 0 && maxCg > 0) {
				points.push({ x: maxCg, y: maxW });
			}
			if (minW > 0 && minCg > 0 && minW < maxW) {
				points.push({ x: minCg, y: minW });
			}
		});

		// Close the polygon
		if (points.length > 0) {
			points.push(points[0]);
		}

		return points;
	};

	// Collect all weights and CGs for chart bounds
	const allWeights = [
		...forwardLimits.flatMap(l => [parseFloat(l.minWeight) || 0, parseFloat(l.maxWeight) || 0]),
		...aftLimits.flatMap(l => [parseFloat(l.minWeight) || 0, parseFloat(l.maxWeight) || 0]),
		...(fuelCasePoints ? fuelCasePoints.map(p => p.weight) : []),
		...(wbPoints ? wbPoints.map(p => parseFloat(p.weight) || 0) : [])
	].filter(w => w > 0);

	const allCgs = [
		...forwardLimits.flatMap(l => [
			parseFloat(l.minWeightCgLimit) || 0,
			parseFloat(l.maxWeightCgLimit) || 0
		]),
		...aftLimits.flatMap(l => [
			parseFloat(l.minWeightCgLimit) || 0,
			parseFloat(l.maxWeightCgLimit) || 0
		]),
		...(fuelCasePoints ? fuelCasePoints.map(p => p.cg) : []),
		...(wbPoints ? wbPoints.map(p => parseFloat(p.cg) || 0) : [])
	].filter(cg => cg > 0);

	if (allWeights.length === 0 || allCgs.length === 0) {
		return (
			<div
				className="flex items-center justify-center text-default-400"
				style={{ minHeight: '200px' }}
			>
				<div className="text-center">
					<BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
					<p>Add forward and aft limits to see the envelope chart</p>
				</div>
			</div>
		);
	}

	const minWeight = Math.min(...allWeights) * 0.95;
	const maxWeight = Math.max(...allWeights) * 1.05;
	const minCg = Math.min(...allCgs) * 0.98;
	const maxCg = Math.max(...allCgs) * 1.02;

	const envelopePoints = createEnvelopePolygon();

	// Chart.js datasets
	const datasets = [
		{
			label: 'W&B Envelope',
			data: envelopePoints,
			showLine: true,
			borderColor: 'rgba(99, 102, 241, 1)',
			backgroundColor: 'rgba(99, 102, 241, 0.15)',
			fill: true,
			pointRadius: 0,
			borderWidth: 2,
			tension: 0,
			order: 10
		}
	];

	// Check if a point is within the envelope polygon
	const isPointInLimits = (weight, cg) => {
		if (envelopePoints.length < 3) return false;
		// Ray casting algorithm for point-in-polygon
		let inside = false;
		for (let i = 0, j = envelopePoints.length - 1; i < envelopePoints.length; j = i++) {
			const xi = envelopePoints[i].x,
				yi = envelopePoints[i].y;
			const xj = envelopePoints[j].x,
				yj = envelopePoints[j].y;
			const intersect =
				yi > weight !== yj > weight && cg < ((xj - xi) * (weight - yi)) / (yj - yi) + xi;
			if (intersect) inside = !inside;
		}
		return inside;
	};

	const MAX_FUEL_COLOR = '#22c55e';   // Maximum (High Case) → Green
	const MIN_FUEL_COLOR = '#93c5fd';   // Minimum (Low Case) → Light Blue
	const OUT_COLOR = '#ef4444';        // Out of envelope → Red
	const SELECTED_COLOR = '#eab308';   // Selected / Active W&B point → Gold/Yellow

	// Add fuel case lines if available (and switched on — see visibleCases).
	let hasMaxCase = false;
	let hasMinCase = false;
	if (fuelCasePoints && fuelCasePoints.length > 0) {
		const maxFuelTakeoff = fuelCasePoints.find(p => p.type === 'maxFuelTakeoff');
		const maxFuelLanding = fuelCasePoints.find(p => p.type === 'maxFuelLanding');
		const minFuelTakeoff = fuelCasePoints.find(p => p.type === 'minFuelTakeoff');
		const minFuelLanding = fuelCasePoints.find(p => p.type === 'minFuelLanding');

		// Max Fuel Case line
		if (
			maxFuelTakeoff &&
			maxFuelLanding &&
			maxFuelTakeoff.weight > 0 &&
			maxFuelLanding.weight > 0
		) {
			const takeoffIn = isPointInLimits(maxFuelTakeoff.weight, maxFuelTakeoff.cg);
			const landingIn = isPointInLimits(maxFuelLanding.weight, maxFuelLanding.cg);
			const takeoffColor = takeoffIn ? MAX_FUEL_COLOR : OUT_COLOR;
			const landingColor = landingIn ? MAX_FUEL_COLOR : OUT_COLOR;

			hasMaxCase = true;
			if (visibleCases.max) datasets.push({
				label: 'Max Fuel Case',
				data: [
					{ x: maxFuelTakeoff.cg, y: maxFuelTakeoff.weight },
					{ x: maxFuelLanding.cg, y: maxFuelLanding.weight }
				],
				showLine: true,
				borderColor: MAX_FUEL_COLOR,
				backgroundColor: MAX_FUEL_COLOR,
				pointRadius: 7,
				pointHoverRadius: 9,
				pointStyle: 'crossRot',
				pointBorderWidth: 2,
				pointBorderColor: [takeoffColor, landingColor],
				pointBackgroundColor: [takeoffColor, landingColor],
				borderWidth: 3,
				tension: 0,
				order: 2
			});
		}

		// Min Fuel Case line
		if (
			minFuelTakeoff &&
			minFuelLanding &&
			minFuelTakeoff.weight > 0 &&
			minFuelLanding.weight > 0
		) {
			const takeoffIn = isPointInLimits(minFuelTakeoff.weight, minFuelTakeoff.cg);
			const landingIn = isPointInLimits(minFuelLanding.weight, minFuelLanding.cg);
			const takeoffColor = takeoffIn ? MIN_FUEL_COLOR : OUT_COLOR;
			const landingColor = landingIn ? MIN_FUEL_COLOR : OUT_COLOR;

			hasMinCase = true;
			if (visibleCases.min) datasets.push({
				label: 'Min Fuel Case',
				data: [
					{ x: minFuelTakeoff.cg, y: minFuelTakeoff.weight },
					{ x: minFuelLanding.cg, y: minFuelLanding.weight }
				],
				showLine: true,
				borderColor: MIN_FUEL_COLOR,
				backgroundColor: MIN_FUEL_COLOR,
				pointRadius: 7,
				pointHoverRadius: 9,
				pointStyle: 'rectRot',
				pointBorderWidth: 2,
				pointBackgroundColor: [takeoffColor, landingColor],
				borderWidth: 3,
				tension: 0,
				order: 1
			});
		}
	}

	// Add W&B points (Ramp/Takeoff/Landing markers)
	if (wbPoints && wbPoints.length > 0) {
		wbPoints.forEach(point => {
			const weight = parseFloat(point.weight);
			const cg = parseFloat(point.cg);
			if (!weight || !cg) return;

			const hasErrors = point.status && point.status.errors && point.status.errors.length > 0;
			const color = hasErrors ? OUT_COLOR : SELECTED_COLOR;
			const pointStyle = WB_POINT_STYLES[point.label] || 'circle';

			datasets.push({
				label: point.label,
				data: [{ x: cg, y: weight }],
				showLine: false,
				borderColor: color,
				backgroundColor: color,
				pointRadius: 7,
				pointHoverRadius: 9,
				pointStyle: pointStyle,
				pointBorderWidth: 2,
				pointBorderColor: '#ffffff',
				order: 1
			});
		});
	}

	// Chart.js data
	const data = { datasets };

	// Axis label text
	const cgAxisLabel = `CG Limit (${unitDisplayStrings.cg[cgUnit] || cgUnit})`;
	const weightAxisLabel = `Weight (${unitDisplayStrings.weight[weightUnit] || weightUnit})`;

	// Tooltip unit suffixes
	const cgTooltipSuffix = cgUnit === 'in' ? '"' : ` ${cgUnit}`;

	// Chart.js options
	const options = {
		responsive: true,
		// Fill the container (rectangular) so the chart spans the full width
		// of its panel; height is fixed by the wrapper div in the render.
		maintainAspectRatio: false,
		layout: {
			padding: { top: 5, right: 10, bottom: 5, left: 5 }
		},
		plugins: {
			title: {
				display: false
			},
			legend: {
				position: 'bottom',
				labels: {
					color: textColor,
					boxWidth: 12,
					padding: 8,
					font: { size: 10 },
					usePointStyle: true
				}
			},
			tooltip: {
				backgroundColor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
				titleColor: textColor,
				bodyColor: textColor,
				borderColor: borderColor,
				borderWidth: 1,
				padding: 12,
				callbacks: {
					label: ctx =>
						`Weight: ${ctx.parsed.y.toFixed(0)} ${weightUnit}, CG: ${ctx.parsed.x.toFixed(2)}${cgTooltipSuffix}`
				}
			}
		},
		scales: {
			x: {
				title: {
					display: true,
					text: cgAxisLabel,
					font: { size: 12, weight: 'bold' },
					color: textColor
				},
				type: 'linear',
				position: 'bottom',
				min: Math.floor(minCg),
				max: Math.ceil(maxCg),
				ticks: { color: textColor, stepSize: Math.ceil((maxCg - minCg) / 10) || 1 },
				grid: { color: gridColor },
				border: { color: borderColor }
			},
			y: {
				title: {
					display: true,
					text: weightAxisLabel,
					font: { size: 12, weight: 'bold' },
					color: textColor
				},
				type: 'linear',
				min: Math.floor(minWeight / 100) * 100,
				max: Math.ceil(maxWeight / 100) * 100,
				ticks: {
					color: textColor,
					// Match the x-axis ~10 divisions so grid cells stay square.
					stepSize:
						Math.ceil(
							(Math.ceil(maxWeight / 100) * 100 - Math.floor(minWeight / 100) * 100) /
								10 /
								100
						) * 100 || 100
				},
				grid: { color: gridColor },
				border: { color: borderColor }
			}
		},
		interaction: {
			mode: 'nearest',
			intersect: false
		}
	};

	const fuelCaseToggles = [
		hasMinCase && { key: 'min', label: 'Min fuel case', color: MIN_FUEL_COLOR },
		hasMaxCase && { key: 'max', label: 'Max fuel case', color: MAX_FUEL_COLOR }
	].filter(Boolean);

	return (
		<div style={{ width: '100%', height, display: 'flex', flexDirection: 'column' }}>
			{fuelCaseToggles.length > 0 && (
				<div className="flex flex-wrap items-center gap-x-4 gap-y-1 pb-2 shrink-0">
					{/* Switch + separate text, as in Security Settings' ModuleCard: legacy
					    Bootstrap `label{display:inline-block}` beats HeroUI's inline-flex,
					    which dropped a nested label under the switch. */}
					{fuelCaseToggles.map(t => (
						<div key={t.key} className="flex items-center gap-2">
							<Switch
								size="sm"
								isSelected={visibleCases[t.key]}
								onValueChange={isOn => toggleCase(t.key, isOn)}
								aria-label={`Show ${t.label}`}
								className="shrink-0"
							/>
							<span
								className="flex cursor-pointer select-none items-center gap-1.5 text-xs text-default-600"
								onClick={() => toggleCase(t.key, !visibleCases[t.key])}
							>
								<span
									aria-hidden="true"
									className="inline-block h-2.5 w-2.5 rounded-sm"
									style={{ backgroundColor: t.color }}
								/>
								{t.label}
							</span>
						</div>
					))}
				</div>
			)}
			<div style={{ position: 'relative', flex: '1 1 auto', minHeight: 0 }}>
				<Scatter ref={chartRef} data={data} options={options} />
			</div>
		</div>
	);
});

export default EnvelopeChart;
